import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SquaresWorkOrdersManagementHandler from 'handlers/squaresHandlers/sqauresWorkOrdersManagement.handler';
import useAppTheme from 'hooks/useAppTheme';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  type NotificationState,
  showNotification,
} from 'store/notification.slice';
import {
  clearAll,
  setActiveStep,
  setErrors,
} from 'store/SquaresStore/WorkOrderManagement/WorkOrderCreationFormState.slice';
import { type WorkOrderCreationPayload } from 'types/Squares/WorkOrderManagement/work.order.creation.form.types';
import { type WorkOrder } from 'types/Squares/WorkOrderManagement/work.order.management.types';
import { type ExternalUser, type InternalUser } from 'types/user.types';
import { getUtcISOStringOfSelectedTz } from 'utils/helpers/dateTime';
import {
  validateCompletionStep,
  validateCreationStep,
} from '../helpers/formValidators';
import {
  getCompletionPayload,
  getCreationPayload,
} from '../helpers/payloadGenerators';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const statusToMaxStepMap: Record<string, number> = {
  open: 1,
  'in progress': 2,
  completed: 2,
};

export const WorkOrderActions = ({
  formType,
  formAction,
  handleBackClick,
}: {
  formType: 'create' | 'request';
  formAction: 'create' | 'update';
  handleBackClick: (value: string) => void;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [loading, setLoading] = useState(false);
  const workOrdersManagementHandler = new SquaresWorkOrdersManagementHandler();
  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();
  const customerId = useAppSelector(
    (state) => state.persistedReducer?.customer?.id
  );
  const authUserEmail = useAppSelector((state) => state?.authReducer?.email);
  const internalUserList = useAppSelector(
    (state) => state.internalUserReducer?.internalUserList
  );
  const externalUserList = useAppSelector(
    (state) => state.externalUserReducer?.externalUserList
  );
  const activeStep = useAppSelector(
    (store) => store?.sqauresWorkOrderCreationFormReducer?.activeStep
  );

  const selectedUser = useMemo(() => {
    let tempInternalUserList: InternalUser[] = [];
    let tempExternalUserList: ExternalUser[] = [];
    if (Array.isArray(internalUserList)) {
      tempInternalUserList = internalUserList;
    }
    if (Array.isArray(externalUserList)) {
      tempExternalUserList = externalUserList;
    }
    const temp = [...tempInternalUserList, ...tempExternalUserList].find(
      (user) => user.email === authUserEmail
    ) ?? {
      userId: '',
      firstName: '',
      lastName: '',
      email: '',
      isActive: true,
      department: '',
      userCustomerAssociations: [],
    };

    return temp;
  }, [authUserEmail, externalUserList, internalUserList]);

  const dispatch = useAppDispatch();

  const formData = useAppSelector(
    (state) => state?.sqauresWorkOrderCreationFormReducer?.values
  );

  // vars
  const status = formData?.status?.toLowerCase();
  const maxAllowedStep = statusToMaxStepMap[status ?? ''] ?? 1;

  const handleClickStep = (targetStep: number) => {
    if (typeof maxAllowedStep === 'number' && targetStep <= maxAllowedStep) {
      if (activeStep === 0) {
        const errors = validateCreationStep(formData);
        if (Object.keys(errors).length > 0) {
          dispatch(setErrors(errors));
          return;
        }
      }
      dispatch(setActiveStep(targetStep));
    }
  };

  const isLoading = useAppSelector(
    (state) => state.sqauresWorkOrderCreationFormReducer.apiState.isLoading
  );

  const createWorkorderAPI = async (
    paramFormData: WorkOrderCreationPayload
  ) => {
    try {
      setLoading(true);
      await workOrdersManagementHandler.createWorkOrder(paramFormData);
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateWorkorderAPI = async (
    workOrderId: string,
    payload: Partial<WorkOrder>
  ) => {
    try {
      setLoading(true);
      await workOrdersManagementHandler.updateWorkOrder(workOrderId, payload);
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkOrder = async (isWorkOrderReuest: boolean) => {
    try {
      console.log({ formData });

      const errors = validateCreationStep(formData);
      console.log({ errors });
      if (Object.keys(errors).length > 0) {
        dispatch(setErrors(errors));
      } else {
        const currentDT = getUtcISOStringOfSelectedTz({
          tz: timezoneBasedOnDisplayMode,
        });
        const payload = getCreationPayload({
          values: formData,
          customerId,
          userId: selectedUser?.userId,
          isWorkOrderReuest,
          currentDateWithTime: currentDT,
          isUpdate: false,
          status: 'open',
        });

        console.log('Creation payload', payload, formData);

        try {
          await createWorkorderAPI(payload);
          const notificationPayload: NotificationState = {
            isLoading: false,
            message: t('work_order_created_successfully'),
            show: true,
            type: 'success',
          };
          dispatch(showNotification(notificationPayload));
        } catch (error) {
          const notificationPayload: NotificationState = {
            isLoading: false,
            message:
              error instanceof Error
                ? error.message
                : typeof error === 'string'
                ? error
                : 'Something Went Wrong',
            show: true,
            type: 'error',
          };
          dispatch(showNotification(notificationPayload));
        }

        dispatch(clearAll());
        handleBackClick('creation');
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdateWorkOrder = async (isWorkOrderReuest: boolean) => {
    const currentDT = getUtcISOStringOfSelectedTz({
      tz: timezoneBasedOnDisplayMode,
    });

    const errors = validateCompletionStep(formData);

    if (Object.keys(errors).length > 0) {
      dispatch(setErrors(errors));
      return;
    }

    const payload = getCompletionPayload({
      values: formData,
      status: 'completed',
      isWorkOrderReuest,
      currentDateWithTime: currentDT,
      userId: selectedUser?.userId,
      isUpdate: true,
    });

    const workOrderId = formData?.workOrderId;
    if (!workOrderId) {
      const notificationPayload: NotificationState = {
        isLoading: false,
        message: t('work_order_id_is_missing'),
        show: true,
        type: 'error',
      };
      dispatch(showNotification(notificationPayload));
      return;
    }

    try {
      await updateWorkorderAPI(workOrderId, payload);
      const notificationPayload: NotificationState = {
        isLoading: false,
        message: `Work order ${
          isWorkOrderReuest ? 'request' : ''
        } updated successfully`,
        show: true,
        type: 'success',
      };
      dispatch(showNotification(notificationPayload));

      // go back to creation view (keeps original behavior)
      handleBackClick('creation');
    } catch (error) {
      const notificationPayload: NotificationState = {
        isLoading: false,
        message:
          error instanceof Error
            ? error.message
            : typeof error === 'string'
            ? error
            : 'Something went wrong',
        show: true,
        type: 'error',
      };
      dispatch(showNotification(notificationPayload));
    } finally {
      dispatch(clearAll());
    }
  };

  const primarySaveButtonConfig = {
    create: {
      create: {
        label: t('create_work_order_3'),
        handler: async () => {
          await handleCreateWorkOrder(false);
        },
        variant: 'contained',
      },
      update: {
        label: (activeStep: number) =>
          activeStep < maxAllowedStep ? 'Update work order' : 'Save work order',
        handler: async () => {
          await handleUpdateWorkOrder(false);
        },
        variant: 'contained',
      },
    },
    request: {
      //  on request phase we will always need to validate and than update auto approved
      create: {
        label: t('create_work_order_2'),
        handler: async () => {
          await handleCreateWorkOrder(false);
          handleBackClick('creation');
        },
        variant: 'outlined',
      },
      update: {
        label: (activeStep: number) => 'Create work order',
        handler: async () => {
          await handleUpdateWorkOrder(false);
          if (activeStep < 2) {
            handleBackClick('creation');
          }
        },
        variant: activeStep < 2 ? 'outlined' : 'contained',
      },
    },
  } satisfies Record<
    'create' | 'request',
    Record<
      'create' | 'update',
      {
        label: string | ((step: number) => string);
        handler: () => void;
        variant: 'contained' | 'outlined' | 'text';
      }
    >
  >;

  // only visible on request
  const secondarySaveButtonConfig = {
    // create part is not used
    create: {
      create: {
        label: t('create_work_order_3'),
        handler: async () => {
          await handleCreateWorkOrder(true);
        },
        variant: 'contained',
      },
      update: {
        label: (activeStep: number) =>
          activeStep < 2 ? 'Update Work Order' : 'Complete Work Order',
        handler: async () => {
          const isWorkOrderReuest = activeStep < 2;
          await handleUpdateWorkOrder(isWorkOrderReuest);
        },
        variant: 'contained',
      },
    },
    request: {
      create: {
        label: t('save_as_request'),
        handler: async () => {
          // on first step he convert req to work order
          await handleCreateWorkOrder(true);
        },
        variant: 'contained',
      },
      update: {
        label: (activeStep: number) =>
          activeStep < 1 ? 'Update' : 'Save as Request',
        handler: async () => {
          // on updating if he convert req to work order
          const isWorkOrderReuest = activeStep < 2;
          await handleUpdateWorkOrder(isWorkOrderReuest);
        },
        variant: 'contained',
      },
    },
  } satisfies Record<
    'create' | 'request',
    Record<
      'create' | 'update',
      {
        label: string | ((step: number) => string);
        handler: () => void;
        variant: 'contained' | 'outlined' | 'text';
      }
    >
  >;

  const currentPrimaryButtonConfig =
    primarySaveButtonConfig[formType][formAction];
  const primarySaveButtonLabel =
    typeof currentPrimaryButtonConfig.label === 'function'
      ? currentPrimaryButtonConfig.label(activeStep)
      : currentPrimaryButtonConfig.label;

  const currentSecondaryButtonConfig =
    secondarySaveButtonConfig[formType][formAction];
  const secondarySaveButtonLabel =
    typeof currentSecondaryButtonConfig.label === 'function'
      ? currentSecondaryButtonConfig.label(activeStep)
      : currentSecondaryButtonConfig.label;

  return (
    <Box
      display={'flex'}
      flexDirection={'row'}
      alignItems={'center'}
      justifyContent={'space-between'}
      sx={{
        backgroundColor: palette.toolBarBackground,
        padding: '12px 24px',
        border: '1px solid #374151',
        width: '100%',
        borderRadius: '0 0 8px 8px',
      }}
    >
      {/* Status Section */}
      <Box display={'flex'} alignItems={'center'} gap={2}>
        <Typography
          variant="body2"
          sx={{
            color: 'white',
            fontWeight: 'medium',
            minWidth: 'fit-content',
          }}
        >
          {t('status')}
        </Typography>
        <ToggleButtonGroup
          color="primary"
          value={activeStep}
          exclusive
          onChange={(
            event: React.MouseEvent<HTMLElement>,
            newValue: string | null
          ) => {
            const parsedVal = Number(newValue);

            if (
              (typeof newValue === 'number' && !isNaN(newValue)) ||
              (typeof newValue === 'string' &&
                newValue.trim() !== '' &&
                !isNaN(parsedVal))
            ) {
              handleClickStep(parsedVal);
            }
          }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              textTransform: 'none',
              fontWeight: 'medium',
              '&.Mui-selected': {
                backgroundColor: palette.primary,
                color: 'white',
                borderColor: palette.primary,
                '&:hover': {
                  backgroundColor: palette.primary,
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            },
          }}
        >
          <ToggleButton value={0}>{t('open')}</ToggleButton>
          <ToggleButton value={1}>{t('dm_log_action_completed')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Actions Section */}
      <Box display={'flex'} flexDirection={'row'} gap={2} alignItems={'center'}>
        {/* primary save button */}
        <Button
          variant={currentPrimaryButtonConfig.variant}
          color="primary"
          sx={{
            textTransform: 'none',
            fontWeight: 'bold',
            backgroundColor:
              currentPrimaryButtonConfig.variant === 'contained'
                ? palette.primary
                : 'transparent',
            color:
              currentPrimaryButtonConfig.variant === 'contained'
                ? 'white'
                : palette.primary,
            borderColor: palette.primary,
            '&:hover': {
              backgroundColor:
                currentPrimaryButtonConfig.variant === 'contained'
                  ? palette.primary
                  : `${palette.primary}1A`,
            },
          }}
          onClick={currentPrimaryButtonConfig.handler}
          disabled={isLoading || loading}
        >
          {primarySaveButtonLabel}
        </Button>

        {/* secondary save button */}
        {formType === 'request' && activeStep !== 2 ? (
          <Button
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              backgroundColor: '#374151',
              color: 'white',
              '&:hover': {
                backgroundColor: '#4b5563',
              },
            }}
            disabled={isLoading || loading}
            onClick={currentSecondaryButtonConfig.handler}
          >
            {secondarySaveButtonLabel}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};
